import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { default as MUIDialog } from '@material-ui/core/Dialog';
import { migrationCloseModal } from './migrationModalActions';
import Button from '@material-ui/core/Button';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import GetAppIcon from '@material-ui/icons/GetApp';
import ReactToPrint from 'react-to-print';

const styles = theme => ({
  dialogContent: {
    minWidth: '600px',
    maxWidth: '800px',
    padding: theme.spacing(1, 3, 3, 3),
    maxHeight: '40vh',
    overflow: 'auto',
  },
  migrationMessageContainer: {
    padding: theme.spacing(0, 3, 0, 3),
  },
  migrationMessage: {
    fontSize: '0.95rem',
    color: theme.palette.text.secondary,
    paddingBottom: theme.spacing(1),
  },
  infoContainer: {
    padding: theme.spacing(2, 3),
    backgroundColor: theme.palette.grey[50],
    borderTop: `1px solid ${theme.palette.grey[300]}`,
  },
  infoText: {
    fontSize: '0.9rem',
    fontWeight: 600,
  },
  summaryRow: {
    marginBottom: theme.spacing(1),
    padding: theme.spacing(0.5, 0),
  },
  label: {
    fontWeight: 600,
    display: 'inline',
  },
  value: {
    fontWeight: 600,
    display: 'inline',
    marginLeft: theme.spacing(1),
  },
  section: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
  },
  documentItem: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(0.5),
  },
  docId: {
    fontFamily: 'monospace',
    fontSize: '0.85rem',
    marginBottom: theme.spacing(0.5),
    wordBreak: 'break-all',
  },
  message: {
    fontSize: '0.9rem',
    wordBreak: 'break-word',
  },
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    justifyContent: 'flex-end',
  },
  printableContent: {
    display: 'none',
    padding: theme.spacing(3),
    '@media print': {
      display: 'block',
    },
  },
  printableContentVisible: {
    padding: theme.spacing(2),
    maxWidth: '100%',
    '@media print': {
      padding: theme.spacing(1),
    },
    '& h5': {
      marginTop: 0,
      marginBottom: theme.spacing(1),
      fontSize: '1.25rem',
    },
    '& $summaryRow': {
      marginBottom: theme.spacing(0.5),
    },
    '& $section': {
      pageBreakInside: 'avoid',
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    '& $documentItem': {
      pageBreakInside: 'avoid',
      padding: theme.spacing(1),
      marginBottom: theme.spacing(0.5),
    },
    '& $divider': {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
  },
});

const mapStateToProps = state => ({
  isOpen: state.MigrationModal.open,
  migrationData: state.MigrationModal.migrationData,
  migrationMessage: state.MigrationModal.migrationMessage,
  blockedDocuments: state.MigrationModal.blockedDocuments,
  linkedDocuments: state.MigrationModal.linkedDocuments,
});

const mapDispatchToProps = {
  onCloseModal: migrationCloseModal,
};

// Constants for blocked documents messages
const SUMMARY_TITLE = 'Migration Summary';
const MULTI_PROJECT_ERROR_MESSAGE = 'Cannot change store: One or more documents are linked to multiple projects.';
const BLOCKED_DOCS_INSTRUCTION = 'Please download report for further reference.';

// Helper function to transform legacy linkedDocuments format to blockedDocuments format
const transformLegacyLinkedDocuments = (linkedDocuments) => {
  if (!linkedDocuments || linkedDocuments.length === 0) return [];
  
  return linkedDocuments.map(doc => ({
    documentName: doc.fileName,
    projectsLinked: [], // Legacy format doesn't have project details
  }));
};

// Helper component for rendering blocked document items with multiple project links
const BlockedDocumentItem = ({ document, className, style }) => (
  <div className={className} style={style}>
    <Typography style={{ fontWeight: 600, fontSize: '0.95rem', marginBottom: '12px' }}>
      {document.documentName}
    </Typography>
    
    {document.projectsLinked && document.projectsLinked.length > 0 && (
      <div style={{ marginTop: '4px' }}>
        {document.projectsLinked.map((project, idx) => (
          <div key={idx} style={{ marginBottom: '8px' }}>
            <Typography style={{ fontSize: '0.85rem', fontWeight: 500 }}>
              Project: {project.projectName}
            </Typography>
            <Typography style={{ fontSize: '0.85rem', color: '#666' }}>
              Category: {project.categoryName}
            </Typography>
          </div>
        ))}
      </div>
    )}
  </div>
);

BlockedDocumentItem.propTypes = {
  document: PropTypes.shape({
    documentName: PropTypes.string.isRequired,
    projectsLinked: PropTypes.arrayOf(
      PropTypes.shape({
        projectName: PropTypes.string.isRequired,
        categoryName: PropTypes.string.isRequired,
      })
    ),
  }).isRequired,
  className: PropTypes.string,
  style: PropTypes.object,
};

// Printable content for blocked documents with multi-project links
const BlockedDocsPrintContent = ({ blockedDocuments }) => (
  <div style={{ padding: '20px', maxWidth: '100%' }}>
    <Typography variant="h5" style={{ marginTop: 0, marginBottom: '16px', fontWeight: 600 }}>
      Store Migration - Documents Linked to Multiple Projects
    </Typography>
    
    <Typography style={{ fontSize: '1rem', marginBottom: '12px', fontWeight: 600, color: '#f44336' }}>
      {MULTI_PROJECT_ERROR_MESSAGE}
    </Typography>    
    
    <div style={{ marginBottom: '16px' }}>
      {blockedDocuments.map((doc, index) => (
        <BlockedDocumentItem
          key={index}
          document={doc}
          style={{ 
            padding: '12px', 
            marginBottom: '8px', 
            backgroundColor: '#f5f5f5',
            pageBreakInside: 'avoid'
          }}
        />
      ))}
    </div>   
   
  </div>
);

BlockedDocsPrintContent.propTypes = {
  blockedDocuments: PropTypes.arrayOf(
    PropTypes.shape({
      documentName: PropTypes.string.isRequired,
      projectsLinked: PropTypes.arrayOf(
        PropTypes.shape({
          projectName: PropTypes.string.isRequired,
          categoryName: PropTypes.string.isRequired,
        })
      ),
    })
  ).isRequired,
};

// Blocked Documents Dialog Component for multi-project linked documents
const BlockedDocumentsDialog = ({ blockedDocuments, isOpen, outerClose, onCloseModal, classes, blockedDocsRef }) => (
  <div>
    {/* Hidden printable version */}
    <div style={{ position: 'absolute', left: '-9999px', top: '0', width: '210mm' }}>
      <div ref={blockedDocsRef}>
        <style>
          {`
            @media print {
              @page {
                margin: 15mm;
                size: A4;
              }
              body {
                margin: 0;
                padding: 0;
              }
            }
          `}
        </style>
        <BlockedDocsPrintContent blockedDocuments={blockedDocuments} />
      </div>
    </div>

    <MUIDialog open={isOpen} onClose={outerClose ? onCloseModal : null} maxWidth="md">
      <Fragment>
        <DialogTitle id="blocked-documents-dialog-title">
          {SUMMARY_TITLE}
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Typography style={{ fontSize: '1rem', marginBottom: '12px', fontWeight: 600, color: '#f44336' }}>
            {MULTI_PROJECT_ERROR_MESSAGE}
          </Typography>
          
          <div style={{ marginTop: '16px', marginBottom: '16px' }}>
            {blockedDocuments.map((doc, index) => (
              <BlockedDocumentItem
                key={index}
                document={doc}
                className={classes.documentItem}
              />
            ))}
          </div>
          
          <div style={{ 
            marginTop: '16px', 
            padding: '16px', 
            backgroundColor: '#fff3e0', 
            borderLeft: '4px solid #ff9800' 
          }}>
            <Typography style={{ fontSize: '0.875rem', color: '#666' }}>
              {BLOCKED_DOCS_INSTRUCTION}
            </Typography>
          </div>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <ReactToPrint
            trigger={() => (
              <Button 
                color="primary" 
                variant="contained"
                startIcon={<GetAppIcon />}
                style={{ marginRight: '8px' }}
              >
                Download Report
              </Button>
            )}
            content={() => blockedDocsRef.current}
            documentTitle={`Blocked_Documents_Report_${new Date().toISOString().split('T')[0]}`}
            pageStyle={`
              @page {
                size: A4;
                margin: 15mm;
              }
              @media print {
                body {
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                }
              }
            `}
          />
          <Button onClick={onCloseModal} color="primary" variant="contained">
            Close
          </Button>
        </DialogActions>
      </Fragment>
    </MUIDialog>
  </div>
);

BlockedDocumentsDialog.propTypes = {
  blockedDocuments: PropTypes.arrayOf(
    PropTypes.shape({
      documentName: PropTypes.string.isRequired,
      projectsLinked: PropTypes.arrayOf(
        PropTypes.shape({
          projectName: PropTypes.string.isRequired,
          categoryName: PropTypes.string.isRequired,
        })
      ),
    })
  ).isRequired,
  isOpen: PropTypes.bool.isRequired,
  outerClose: PropTypes.bool,
  onCloseModal: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
  blockedDocsRef: PropTypes.object.isRequired,
};

export const MigrationSummaryModal = ({ migrationData, migrationMessage, blockedDocuments, linkedDocuments, isOpen, outerClose, onCloseModal, classes }) => {
  const componentRef = React.useRef();
  const printableRef = React.useRef();
  const blockedDocsRef = React.useRef();
  
  // Handle blocked documents error case (multi-project linked documents)
  // Prefer blockedDocuments (new API format) over linkedDocuments (old API format)
  const documentsToDisplay = blockedDocuments && blockedDocuments.length > 0 
    ? blockedDocuments 
    : linkedDocuments && linkedDocuments.length > 0
      ? transformLegacyLinkedDocuments(linkedDocuments)
      : null;
  
  if (documentsToDisplay && documentsToDisplay.length > 0) {
    return (
      <BlockedDocumentsDialog
        blockedDocuments={documentsToDisplay}
        isOpen={isOpen}
        outerClose={outerClose}
        onCloseModal={onCloseModal}
        classes={classes}
        blockedDocsRef={blockedDocsRef}
      />
    );
  }
  
  if (!migrationData) return null;

  const {
    totalDocuments,
    successCount,
    failedCount,
    failedDocuments,
    DuplicateFiles,
    duplicateCount,
  } = migrationData;

  const failedDocs = failedDocuments || [];
  const duplicateDocs = DuplicateFiles || [];
  const duplicatesCount = duplicateCount || duplicateDocs.length;

  const hasFailures = failedCount > 0 && failedDocs.length > 0;
  const hasDuplicates = duplicatesCount > 0;

  // Reusable content component
  const ReportContent = ({ isPrintable }) => (
    <div className={isPrintable ? classes.printableContentVisible : ''}>
      <Typography variant="h5" style={{ marginTop: 0, marginBottom: '8px', fontWeight: 600 }}>
        Migration Summary
      </Typography>
      
      {migrationMessage && (
        <Typography style={{ fontSize: '0.95rem', marginBottom: '12px' }}>
          {migrationMessage}
        </Typography>
      )}

      {/* Summary Counts */}
      <div className={classes.summaryRow}>
        <Typography className={classes.label}>Total Files:</Typography>
        <Typography className={classes.value}>{totalDocuments}</Typography>
      </div>

      <div className={classes.summaryRow}>
        <Typography className={classes.label}>Success Documents:</Typography>
        <Typography className={classes.value}>{successCount}</Typography>
      </div>

      {hasFailures && (
        <div className={classes.summaryRow}>
          <Typography className={classes.label}>Failed Documents:</Typography>
          <Typography className={classes.value}>{failedCount}</Typography>
        </div>
      )}

      {hasDuplicates && (
        <div className={classes.summaryRow}>
          <Typography className={classes.label}>Duplicate Documents:</Typography>
          <Typography className={classes.value}>{duplicatesCount}</Typography>
        </div>
      )}

      {/* Failed Documents Section */}
      {hasFailures && (
        <>
          <Divider className={classes.divider} />
          <div className={classes.section}>
            <Typography className={classes.sectionTitle}>
              Failed Documents: {failedCount}
            </Typography>
            {failedDocs.map((doc, index) => (
              <div key={index} className={classes.documentItem}>
                <Typography className={classes.message}>
                  {doc.docId} - {doc.fileName || doc.message}
                </Typography>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Duplicate Documents Section */}
      {hasDuplicates && (
        <>
          <Divider className={classes.divider} />
          <div className={classes.section}>
            <Typography className={classes.sectionTitle}>
              Duplicate Documents: {duplicatesCount}
            </Typography>
            {duplicateDocs.map((doc, index) => (
              <div key={index} className={classes.documentItem}>
                <Typography className={classes.message}>
                  {doc.documentId} - {doc.updatedName}
                </Typography>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Note Section */}
      {(hasFailures || hasDuplicates) && (
        <>
          <Divider className={classes.divider} />
          <Typography style={{ fontSize: '0.9rem', fontWeight: 600, marginTop: '12px' }}>
            <strong>Note:</strong> Please review migration status and download report for reference.
          </Typography>
        </>
      )}
    </div>
  );

  return (
    <div>
      {/* Hidden printable version with full content */}
      <div style={{ position: 'absolute', left: '-9999px', top: '0', width: '210mm' }}>
        <div ref={printableRef}>
          <style>
            {`
              @media print {
                @page {
                  margin: 15mm;
                  size: A4;
                }
                body {
                  margin: 0;
                  padding: 0;
                }
              }
            `}
          </style>
          <ReportContent isPrintable={true} />
        </div>
      </div>

      {/* Visible dialog */}
      <MUIDialog open={isOpen} onClose={outerClose ? onCloseModal : null} maxWidth="md" fullWidth>
        <div ref={componentRef}>
          <Fragment>
            <DialogTitle id="migration-summary-dialog-title">
              Migration Summary
            </DialogTitle>
            
            {/* Migration Message - Fixed below title */}
            {migrationMessage && (
              <div className={classes.migrationMessageContainer}>
                <Typography className={classes.migrationMessage}>
                  {migrationMessage}
                </Typography>
              </div>
            )}

            <DialogContent className={classes.dialogContent}>
              {/* Summary Counts */}
              <div className={classes.summaryRow}>
                <Typography className={classes.label}>Total Files:</Typography>
                <Typography className={classes.value}>{totalDocuments}</Typography>
              </div>

              <div className={classes.summaryRow}>
                <Typography className={classes.label}>Success Documents:</Typography>
                <Typography className={classes.value}>{successCount}</Typography>
              </div>

              {hasFailures && (
                <div className={classes.summaryRow}>
                  <Typography className={classes.label}>Failed Documents:</Typography>
                  <Typography className={classes.value}>{failedCount}</Typography>
                </div>
              )}

              {hasDuplicates && (
                <div className={classes.summaryRow}>
                  <Typography className={classes.label}>Duplicate Documents:</Typography>
                  <Typography className={classes.value}>{duplicatesCount}</Typography>
                </div>
              )}

              {/* Failed Documents Section */}
              {hasFailures && (
                <>
                  <Divider className={classes.divider} />
                  <div className={classes.section}>
                    <Typography className={classes.sectionTitle}>
                      Failed Documents: {failedCount}
                    </Typography>
                    {failedDocs.map((doc, index) => (
                      <div key={index} className={classes.documentItem}>
                        <Typography className={classes.message}>
                          {doc.docId} - {doc.fileName || doc.message}
                        </Typography>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Duplicate Documents Section */}
              {hasDuplicates && (
                <>
                  <Divider className={classes.divider} />
                  <div className={classes.section}>
                    <Typography className={classes.sectionTitle}>
                      Duplicate Documents: {duplicatesCount}
                    </Typography>
                    {duplicateDocs.map((doc, index) => (
                      <div key={index} className={classes.documentItem}>
                        <Typography className={classes.message}>
                          {doc.documentId} - {doc.updatedName}
                        </Typography>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </DialogContent>
            
            {/* Note Section */}
            {(hasFailures || hasDuplicates) && (
              <div className={classes.infoContainer}>
                <Typography className={classes.infoText}>
                  <strong>Note:</strong> Please review migration status and download report for reference.
                </Typography>
              </div>
            )}
            
            <DialogActions className={classes.dialogActions}>
              {(hasFailures || hasDuplicates) && (
                <ReactToPrint
                  trigger={() => (
                    <Button 
                      color="primary" 
                      variant="contained"
                      startIcon={<GetAppIcon />}
                      style={{ marginRight: '8px' }}
                    >
                      Download Report
                    </Button>
                  )}
                  content={() => printableRef.current}
                  documentTitle={`Migration_Summary_${new Date().toISOString().split('T')[0]}`}
                  pageStyle={`
                    @page {
                      size: A4;
                      margin: 15mm;
                    }
                    @media print {
                      body {
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                      }
                    }
                  `}
                />
              )}
              <Button onClick={onCloseModal} color="primary" variant="contained">
                Close
              </Button>
            </DialogActions>
          </Fragment>
        </div>
      </MUIDialog>
    </div>
  );
};

MigrationSummaryModal.defaultProps = {
  migrationData: null,
  isOpen: false,
  outerClose: false,
};

MigrationSummaryModal.propTypes = {
  migrationData: PropTypes.shape({
    oldStoreNumber: PropTypes.string,
    newStoreNumber: PropTypes.string,
    totalDocuments: PropTypes.number,
    successCount: PropTypes.number,
    failedCount: PropTypes.number,
    failedDocuments: PropTypes.arrayOf(
      PropTypes.shape({
        docId: PropTypes.string,
        fileName: PropTypes.string,
        message: PropTypes.string,
      })
    ),
    DuplicateFiles: PropTypes.arrayOf(
      PropTypes.shape({
        documentId: PropTypes.string,
        updatedName: PropTypes.string,
      })
    ),
    duplicateCount: PropTypes.number,
    storeNumberChanged: PropTypes.bool,
    storeDetailsChanged: PropTypes.bool,
    dwmError: PropTypes.string,
  }),
  migrationMessage: PropTypes.string,
  blockedDocuments: PropTypes.arrayOf(
    PropTypes.shape({
      documentName: PropTypes.string.isRequired,
      projectsLinked: PropTypes.arrayOf(
        PropTypes.shape({
          projectName: PropTypes.string.isRequired,
          categoryName: PropTypes.string.isRequired,
        })
      ),
    })
  ),
  linkedDocuments: PropTypes.arrayOf(
    PropTypes.shape({
      fileName: PropTypes.string.isRequired,
      categoryName: PropTypes.string.isRequired,
    })
  ),
  isOpen: PropTypes.bool,
  outerClose: PropTypes.bool,
  onCloseModal: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(MigrationSummaryModal));
